/**
 * @fileoverview O assistente que cria o agente de IA (30/10/2026, etapa 5 dos
 * planos), em três passos: a pessoa cola o site (ou descreve o negócio), a IA
 * prepara a base de conhecimento (o servidor trabalha em segundo plano e a
 * tela acompanha) e mostra o que aprendeu. Ali dá para ver e ajustar a base,
 * conversar com a IA como se fosse um cliente e ativá-la. Ela só atende depois
 * de "Ativar minha IA", que a põe no fluxo "Atendimento com IA" (em Fluxos).
 * Voltar ao assistente prepara uma versão nova: a conversa de teste usa essa
 * versão, e ela só entra no ar ao ativar. Usado na esteira do onboarding e pelo
 * checklist do Dashboard. Sem o e-mail confirmado, pede a confirmação no lugar
 * do formulário (a IA só nasce com ele, etapa 12 dos planos).
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ArrowLeft, Building2, Check, Clock, FileText, Globe, HelpCircle, Link2, Package, Phone, ShieldCheck } from "lucide-react";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import { i18n } from "../../translate/i18n";
import { useAuthFormStyles } from "../AuthLayout/formStyles";
import { errorCode, errorText } from "../AuthLayout/authHelpers";
import { useAiSetupStyles } from "./styles";
import ConfirmEmailCard from "./ConfirmEmailCard";
import KnowledgeDialog from "./KnowledgeDialog";
import TestChatDialog from "./TestChatDialog";
import LearnedStep, { greetingOf, questionsOf } from "./LearnedStep";

const DESCRIPTION_MIN = 40;
const POLL_MS = 2000;
// Além disto (o servidor também desiste), a espera termina com a falha.
const MAX_WAIT_MS = 7 * 60 * 1000;
// Quanto costuma levar (só para a barra andar num ritmo crível; o fim é o do servidor).
const EXPECTED_MS = { site: 40000, description: 15000 };
const STAGES = {
  site: ["readSite", "offerings", "faq", "review"],
  description: ["readDescription", "offerings", "faq", "review"],
};
const LEARN_ITEMS = [
  { key: "about", Icon: Building2 },
  { key: "offerings", Icon: Package },
  { key: "hours", Icon: Clock },
  { key: "faq", Icon: HelpCircle },
  { key: "contact", Icon: Phone },
  { key: "policies", Icon: ShieldCheck },
];

const jobErrorText = (code) =>
  code && i18n.exists(`backendErrors.${code}`) ? i18n.t(`backendErrors.${code}`) : i18n.t("aiSetup.errors.generic");

const StepHead = ({ classes, step, title, subtitle, done }) => (
  <div className={classes.head} style={title ? undefined : { marginBottom: 18 }}>
    <div className={classes.stepLabel}>
      <span>{i18n.t("aiSetup.step", { current: step, total: 3 })}</span>
      <span className={classes.stepBar} aria-hidden="true">
        {[1, 2, 3].map((n) => (
          <span key={n} className={`${classes.stepSegment}${n <= step ? ` ${classes.stepSegmentOn}` : ""}`} />
        ))}
      </span>
      {done && (
        <span className={classes.stepDone}>
          <Check size={14} strokeWidth={3} /> {i18n.t("aiSetup.learned.stepDone")}
        </span>
      )}
    </div>
    {title && <h1 className={classes.title}>{title}</h1>}
    {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
  </div>
);

// Os títulos que o servidor escreve na base (knowledgeBase.SECTIONS), para
// abrir a base já na seção que faltou. O que falta é ele que diz (missing).
const SECTION_HEADINGS = { hours: "Horários de atendimento", contact: "Contato e endereços", policies: "Políticas e informações importantes" };
// Uma consulta que falha (rede, servidor reiniciando) não encerra o preparo; só várias seguidas.
const POLL_FAILURES = 5;

const AiSetupAssistant = ({ onFinish, onSkip }) => {
  const classes = useAiSetupStyles();
  const form = useAuthFormStyles();

  const [step, setStep] = useState("source");
  const [mode, setMode] = useState("site");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [starting, setStarting] = useState(false);

  const [job, setJob] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [finishing, setFinishing] = useState(false);

  const [result, setResult] = useState(null);
  const [agent, setAgent] = useState(null);
  const [agentName, setAgentName] = useState(() => i18n.t("aiSetup.learned.defaultName"));
  const [savedName, setSavedName] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [focusSection, setFocusSection] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testQuestion, setTestQuestion] = useState(null);
  // A comemoração só quando o aprendizado termina agora (não ao voltar ao passo).
  const [celebrate, setCelebrate] = useState(false);
  const { user, impersonation } = useContext(AuthContext);
  // Sem o e-mail confirmado (a sessão diz, ou o servidor recusou): o pedido de confirmação no lugar do formulário. O "Acessar como" passa.
  const [emailRefused, setEmailRefused] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const emailPending = !emailConfirmed && !impersonation && !user?.super && (emailRefused || user?.emailVerified === false);
  const handleEmailConfirmed = useCallback(() => setEmailConfirmed(true), []);
  // Ativar cria (ou muda) o fluxo "Atendimento com IA": pede também editar os fluxos.
  const canActivate = usePermission().can("flows.edit", isAdminUser(user));

  const mountedRef = useRef(true);
  // A gravação em andamento e a última que terminou (com o nome dela): o "Ativar" usa a versão do nome que está no campo.
  const pendingSaveRef = useRef(null);
  const lastSavedRef = useRef(null);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  // Passo 1: começar o preparo
  const start = async (event) => {
    event.preventDefault();
    if (starting) return;
    if (mode === "site" && !url.trim()) return setFieldError(i18n.t("aiSetup.source.siteRequired"));
    if (mode === "description" && description.trim().length < DESCRIPTION_MIN) return setFieldError(i18n.t("backendErrors.ERR_AI_SETUP_DESCRIPTION_SHORT"));
    setStarting(true);
    setFieldError("");
    try {
      const body = mode === "site" ? { source: "site", url: url.trim() } : { source: "description", description: description.trim() };
      const { data } = await api.post("/ai-setup/knowledge", body);
      if (!mountedRef.current) return;
      setJob(data);
      setNow(Date.now());
      setStep("preparing");
    } catch (err) {
      if (mountedRef.current) {
        if (errorCode(err) === "ERR_EMAIL_NOT_VERIFIED") {
          setEmailConfirmed(false);
          setEmailRefused(true);
        } else {
          setFieldError(errorText(err));
        }
      }
    } finally {
      if (mountedRef.current) setStarting(false);
    }
  };

  // Passo 2: acompanhar o trabalho no servidor
  const running = step === "preparing" && job?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    let stopped = false;
    let failures = 0;
    const clock = setInterval(() => setNow(Date.now()), 500);
    const poll = setInterval(async () => {
      if (Date.now() - new Date(job.startedAt).getTime() > MAX_WAIT_MS) {
        setJob((current) => ({ ...current, status: "failed", error: "ERR_AI_SETUP_FAILED" }));
        return;
      }
      try {
        const { data } = await api.get(`/ai-setup/knowledge/${job.id}`);
        failures = 0;
        if (!stopped && data.status !== "running") setJob(data);
      } catch (err) {
        failures += 1;
        // O trabalho que não existe mais (404) encerra na hora.
        if (!stopped && (err?.response?.status === 404 || failures >= POLL_FAILURES)) {
          setJob((current) => ({ ...current, status: "failed", error: errorCode(err) || "ERR_AI_SETUP_FAILED" }));
        }
      }
    }, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(clock);
      clearInterval(poll);
    };
  }, [running, job]);

  const saveAgent = useCallback(
    async ({ name, knowledge, businessName }) => {
      const request = api.post("/ai-setup/agent", { agentId: agent?.agentId, name, knowledge, businessName });
      pendingSaveRef.current = request;
      try {
        const { data } = await request;
        lastSavedRef.current = { name, data };
        if (mountedRef.current) {
          setAgent(data);
          setSavedName(name);
        }
        return data;
      } finally {
        if (pendingSaveRef.current === request) pendingSaveRef.current = null;
      }
    },
    [agent]
  );

  // Pronto: a lista termina de marcar e o passo 3 abre, com o agente (desligado) já salvo para o teste.
  const done = step === "preparing" && job?.status === "done";
  useEffect(() => {
    if (!done) return undefined;
    setFinishing(true);
    const timer = setTimeout(async () => {
      if (!mountedRef.current) return;
      setResult(job.result);
      setFinishing(false);
      setCelebrate(true);
      setStep("learned");
      setActionError("");
      try {
        await saveAgent({
          name: agentName.trim() || i18n.t("aiSetup.learned.defaultName"),
          knowledge: job.result.knowledge,
          businessName: job.result.profile?.businessName,
        });
      } catch (err) {
        if (mountedRef.current) setActionError(errorText(err));
      }
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const restart = (nextMode = mode) => {
    setJob(null);
    setResult(null);
    setFinishing(false);
    setMode(nextMode);
    setFieldError("");
    setStep("source");
  };

  // Passo 3
  // O agente que não salvou ao abrir o passo (rede, chave da IA): de novo, com o nome de agora.
  const retrySave = async () => {
    if (saving) return;
    setSaving(true);
    setActionError("");
    try {
      await saveAgent({ name: agentName.trim() || i18n.t("aiSetup.learned.defaultName"), knowledge: result.knowledge, businessName: result.profile?.businessName });
    } catch (err) {
      if (mountedRef.current) setActionError(errorText(err));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const commitName = async () => {
    const name = agentName.trim();
    if (!agent || !result || name === savedName) return;
    if (name.length < 2 || name.length > 60) return setActionError(i18n.t("backendErrors.ERR_AI_SETUP_INVALID_NAME"));
    setActionError("");
    try {
      await saveAgent({ name, knowledge: result.knowledge, businessName: result.profile?.businessName });
    } catch (err) {
      if (mountedRef.current) setActionError(errorText(err));
    }
  };

  const saveKnowledge = async (knowledge) => {
    const saved = await saveAgent({ name: agentName.trim() || i18n.t("aiSetup.learned.defaultName"), knowledge, businessName: result.profile?.businessName });
    if (mountedRef.current) setResult((current) => ({ ...current, knowledge, missing: saved.missing || current.missing }));
  };

  const openBase = (section = null) => {
    setTestOpen(false);
    setFocusSection(section ? SECTION_HEADINGS[section] : null);
    setKnowledgeOpen(true);
  };

  const ask = (question) => {
    setTestQuestion(question);
    setTestOpen(true);
  };

  const activate = async () => {
    if (saving || !canActivate) return;
    setSaving(true);
    setActionError("");
    try {
      const name = agentName.trim() || i18n.t("aiSetup.learned.defaultName");
      // O nome mudado logo antes ainda pode estar salvando (o campo perde o foco no clique): espera, e
      // salva aqui se a última versão não tem este nome. A versão ativada é sempre a do nome dado.
      if (pendingSaveRef.current) await pendingSaveRef.current.catch(() => null);
      const last = lastSavedRef.current;
      const saved = last && last.name === name ? last.data : await saveAgent({ name, knowledge: result.knowledge, businessName: result.profile?.businessName });
      // A versão testada e o nome entram no ar juntos.
      await api.post(`/ai-setup/agent/${saved.agentId}/activate`, { versionId: saved.versionId, name });
      if (mountedRef.current) setSaving(false);
      if (onFinish) onFinish({ agentId: saved.agentId, active: true });
    } catch (err) {
      if (mountedRef.current) {
        setActionError(errorText(err));
        setSaving(false);
      }
    }
  };

  if (step === "preparing") {
    const source = job?.source || mode;
    const failed = job?.status === "failed";
    const elapsed = Math.max(0, now - new Date(job?.startedAt || Date.now()).getTime());
    const fraction = finishing ? 1 : Math.min(0.92, 0.92 * (1 - Math.exp(-elapsed / (EXPECTED_MS[source] * 0.6))));
    const activeIndex = finishing ? STAGES[source].length : Math.min(STAGES[source].length - 1, Math.floor((fraction / 0.92) * STAGES[source].length));
    return (
      <div className={`${classes.root} ${classes.view}`}>
        <StepHead
          classes={classes}
          step={2}
          title={failed ? i18n.t("aiSetup.preparing.failedTitle") : i18n.t("aiSetup.preparing.title")}
          subtitle={failed ? jobErrorText(job.error) : i18n.t(`aiSetup.preparing.subtitle.${source}`)}
        />
        <div className={`${classes.card} ${classes.preparing}`}>
          {failed ? (
            <div className={classes.actions} style={{ marginTop: 0 }}>
              <button type="button" className={`${form.button} ${classes.primaryInline}`} onClick={() => restart(source)}>
                {i18n.t("aiSetup.preparing.retry")}
              </button>
              {source === "site" && (
                <button type="button" className={`${form.secondary} ${classes.secondaryInline}`} onClick={() => restart("description")}>
                  {i18n.t("aiSetup.preparing.describeInstead")}
                </button>
              )}
            </div>
          ) : (
            <>
              <ul className={classes.stageList} aria-live="polite">
                {STAGES[source].map((key, index) => {
                  const state = index < activeIndex ? "done" : index === activeIndex ? "active" : "queued";
                  return (
                    <li key={key} className={`${classes.stage}${state === "active" ? ` ${classes.stageActive}` : state === "done" ? ` ${classes.stageDone}` : ""}`}>
                      <span className={`${classes.stageMark}${state === "active" ? ` ${classes.stageMarkActive}` : state === "done" ? ` ${classes.stageMarkDone}` : ""}`}>
                        {state === "done" && <Check size={13} strokeWidth={3} />}
                      </span>
                      {i18n.t(`aiSetup.preparing.stages.${key}`)}
                      <span className={classes.stageStatus}>{i18n.t(`aiSetup.preparing.status.${state}`)}</span>
                    </li>
                  );
                })}
              </ul>
              <div className={classes.progress} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(fraction * 100)}>
                <div className={classes.progressFill} style={{ width: `${Math.round(fraction * 100)}%` }} />
              </div>
              <div className={classes.progressRow}>
                <span>{i18n.t(`aiSetup.preparing.eta.${source}`)}</span>
                <span>{Math.round(fraction * 100)}%</span>
              </div>
            </>
          )}
        </div>
        <div className={classes.footNote}>
          <ShieldCheck size={14} /> {i18n.t("aiSetup.reviewNote")}
        </div>
      </div>
    );
  }

  if (step === "learned" && result) {
    const name = agentName.trim() || savedName || i18n.t("aiSetup.learned.defaultName");
    const businessName = result.profile?.businessName || user?.company?.groupName || user?.company?.name || i18n.t("aiSetup.learned.yourBusiness");
    return (
      <>
        <LearnedStep
          head={<StepHead classes={classes} step={3} done />}
          result={result}
          businessName={businessName}
          missing={result.missing || []}
          celebrate={celebrate}
          agentReady={Boolean(agent)}
          agentName={agentName}
          onNameChange={setAgentName}
          onNameCommit={commitName}
          saving={saving}
          actionError={actionError}
          onRetrySave={retrySave}
          canActivate={canActivate}
          onActivate={activate}
          onSkipActivation={() => onFinish && onFinish({ agentId: agent?.agentId ?? null, active: false })}
          onOpenBase={openBase}
          onAsk={ask}
        />
        <KnowledgeDialog
          open={knowledgeOpen}
          onClose={() => setKnowledgeOpen(false)}
          knowledge={result.knowledge}
          pages={result.pages || []}
          focusSection={focusSection}
          onSave={saveKnowledge}
        />
        <TestChatDialog
          open={testOpen}
          onClose={() => setTestOpen(false)}
          agentId={agent?.agentId}
          versionId={agent?.versionId}
          agentName={name}
          initialQuestion={testQuestion}
          greeting={greetingOf(name, businessName, result.profile)}
          suggestions={questionsOf(result.profile)}
          onFixBase={() => openBase()}
        />
      </>
    );
  }

  return (
    <div className={`${classes.root} ${classes.view}`}>
      <StepHead
        classes={classes}
        step={1}
        title={i18n.t("aiSetup.source.title")}
        subtitle={i18n.t(mode === "site" ? "aiSetup.source.subtitle" : "aiSetup.source.subtitleDescription")}
      />
      <div className={classes.sourceGrid}>
        {emailPending ? (
          <ConfirmEmailCard email={user?.email} onConfirmed={handleEmailConfirmed} />
        ) : (
          <form className={classes.card} noValidate onSubmit={start}>
            {mode === "site" ? (
              <>
                <h2 className={classes.cardTitle}>
                  <span className={classes.iconBox}><Globe size={17} /></span>
                  {i18n.t("aiSetup.source.siteTitle")}
                </h2>
                <p className={classes.cardText}>{i18n.t("aiSetup.source.siteText")}</p>
                <div className={form.field}>
                  <label className={form.label} htmlFor="ai-setup-site">{i18n.t("aiSetup.source.siteLabel")}</label>
                  <div className={form.inputWrap}>
                    <Link2 size={17} className={classes.inputIcon} aria-hidden="true" />
                    <input
                      id="ai-setup-site"
                      className={`${form.input} ${classes.inputWithIcon}`}
                      value={url}
                      onChange={(event) => {
                        setUrl(event.target.value);
                        if (fieldError) setFieldError("");
                      }}
                      placeholder="suaempresa.com.br"
                      inputMode="url"
                      autoComplete="url"
                      autoFocus
                      aria-invalid={Boolean(fieldError)}
                      aria-describedby={fieldError ? "ai-setup-error" : "ai-setup-site-hint"}
                    />
                  </div>
                  {fieldError ? (
                    <div id="ai-setup-error" className={form.error}>{fieldError}</div>
                  ) : (
                    <div id="ai-setup-site-hint" className={form.hint}>{i18n.t("aiSetup.source.siteHint")}</div>
                  )}
                </div>
                <button type="submit" className={form.button} disabled={starting}>
                  {starting && <CircularProgress size={18} className={form.spinner} />}
                  {i18n.t("aiSetup.source.siteSubmit")}
                </button>
                <div className={classes.divider}>{i18n.t("aiSetup.source.or")}</div>
                <button type="button" className={classes.textButton} onClick={() => { setMode("description"); setFieldError(""); }}>
                  <FileText size={15} /> {i18n.t("aiSetup.source.noSite")}
                </button>
              </>
            ) : (
              <>
                <h2 className={classes.cardTitle}>
                  <span className={classes.iconBox}><FileText size={17} /></span>
                  {i18n.t("aiSetup.source.descriptionTitle")}
                </h2>
                <p className={classes.cardText}>{i18n.t("aiSetup.source.descriptionText")}</p>
                <div className={form.field}>
                  <label className={form.label} htmlFor="ai-setup-description">{i18n.t("aiSetup.source.descriptionLabel")}</label>
                  <textarea
                    id="ai-setup-description"
                    className={`${form.input} ${classes.textarea}`}
                    value={description}
                    onChange={(event) => {
                      setDescription(event.target.value);
                      if (fieldError) setFieldError("");
                    }}
                    placeholder={i18n.t("aiSetup.source.descriptionPlaceholder")}
                    maxLength={6000}
                    autoFocus
                    aria-invalid={Boolean(fieldError)}
                    aria-describedby="ai-setup-description-hint"
                  />
                  {fieldError ? (
                    <div className={form.error} id="ai-setup-description-hint">{fieldError}</div>
                  ) : (
                    <div className={form.hint} id="ai-setup-description-hint">
                      {description.trim().length < DESCRIPTION_MIN
                        ? i18n.t("aiSetup.source.descriptionCountShort", { count: description.trim().length, min: DESCRIPTION_MIN })
                        : i18n.t("aiSetup.source.descriptionCount", { count: description.trim().length })}
                    </div>
                  )}
                </div>
                <button type="submit" className={form.button} disabled={starting}>
                  {starting && <CircularProgress size={18} className={form.spinner} />}
                  {i18n.t("aiSetup.source.descriptionSubmit")}
                </button>
                <div className={classes.divider}>{i18n.t("aiSetup.source.or")}</div>
                <button type="button" className={classes.textButton} onClick={() => { setMode("site"); setFieldError(""); }}>
                  <ArrowLeft size={15} /> {i18n.t("aiSetup.source.haveSite")}
                </button>
              </>
            )}
            <div className={classes.footNote}>
              <ShieldCheck size={14} /> {i18n.t("aiSetup.reviewNote")}
            </div>
          </form>
        )}
        <aside className={classes.aside} aria-labelledby="ai-learn-title">
          <h2 id="ai-learn-title" className={classes.asideTitle}>{i18n.t("aiSetup.source.learnTitle")}</h2>
          <ul className={classes.learnList}>
            {LEARN_ITEMS.map(({ key, Icon }) => (
              <li key={key} className={classes.learnItem}>
                <span className={classes.iconBox}><Icon size={16} /></span>
                <span>
                  <span className={classes.learnName}>{i18n.t(`aiSetup.learn.${key}.name`)}</span>
                  <span className={classes.learnText}>{i18n.t(`aiSetup.learn.${key}.text`)}</span>
                </span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
      {onSkip && (
        <div className={classes.skipRow}>
          <button type="button" className={form.link} onClick={onSkip}>{i18n.t("aiSetup.skip")}</button>
        </div>
      )}
    </div>
  );
};

export default AiSetupAssistant;
